import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Assessment as ReportsIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendIcon,
  AttachMoney as RevenueIcon,
  Message as MessageIcon,
  WarningAmber as QualityIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../../services/apiService";
import { crmColors } from "../../../theme/colors";

const formatCurrency = (n) =>
  `₹${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const KpiCard = memo(function KpiCard({ title, value, icon, tint }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${alpha(tint || crmColors.primary.main, 0.18)}`,
        bgcolor: alpha(tint || crmColors.primary.main, 0.04),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(tint || crmColors.primary.main, 0.12),
            color: tint || crmColors.primary.main,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
            {title}
          </Typography>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
});

/**
 * Portfolio reports — usage, revenue, quality across school WABAs.
 */
const WhatsAppReports = memo(function WhatsAppReports({ refreshSignal = 0 }) {
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [data, setData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getWhatsAppAdminReports({ from_date: fromDate, to_date: toDate });
    if (!result.success) {
      toast.error(result.error || "Failed to load reports");
      setLoading(false);
      return;
    }
    setData(result.data || {});
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const overview = data.overview || {};
  const byCategory = Array.isArray(data.by_category) ? data.by_category : [];
  const topSchools = Array.isArray(data.top_schools) ? data.top_schools : [];
  const dailyTrend = Array.isArray(data.daily_trend) ? data.daily_trend : [];
  const maxTrend = Math.max(1, ...dailyTrend.map((d) => Number(d.total_messages) || 0));

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${crmColors.border.default}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            <ReportsIcon sx={{ color: crmColors.primary.main }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Reports
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Usage, revenue & quality across school WABAs
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              type="date"
              label="From"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} sx={{ color: crmColors.primary.main }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Messages (month)"
                value={(overview.messages_sent_this_month ?? 0).toLocaleString()}
                icon={<MessageIcon fontSize="small" />}
                tint={crmColors.primary.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Revenue (month)"
                value={formatCurrency(overview.revenue_this_month)}
                icon={<RevenueIcon fontSize="small" />}
                tint={crmColors.energy.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Active subscriptions"
                value={(overview.active_subscriptions ?? 0).toLocaleString()}
                icon={<TrendIcon fontSize="small" />}
                tint={crmColors.success.main}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Quality flagged"
                value={(overview.quality_flagged_schools ?? 0).toLocaleString()}
                icon={<QualityIcon fontSize="small" />}
                tint={crmColors.warning.main}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${crmColors.border.default}`, overflow: "hidden" }}>
                <Box sx={{ px: 1.75, py: 1.1, borderBottom: `1px solid ${crmColors.divider}`, bgcolor: alpha(crmColors.primary.main, 0.05) }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    By category
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Category", "Messages", "Failed", "Cost"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.7rem" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byCategory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            No message data in range
                          </TableCell>
                        </TableRow>
                      ) : (
                        byCategory.map((row) => (
                          <TableRow key={row.category}>
                            <TableCell sx={{ textTransform: "capitalize", fontWeight: 600 }}>{row.category}</TableCell>
                            <TableCell>{row.total_messages}</TableCell>
                            <TableCell>{row.failed_count}</TableCell>
                            <TableCell>₹{Number(row.total_cost || 0).toFixed(4)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${crmColors.border.default}`, overflow: "hidden", height: "100%" }}>
                <Box sx={{ px: 1.75, py: 1.1, borderBottom: `1px solid ${crmColors.divider}`, bgcolor: alpha(crmColors.primary.main, 0.05) }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Top schools by volume
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["School", "Messages", "Failed", "Cost"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.7rem" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topSchools.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            No school activity in range
                          </TableCell>
                        </TableRow>
                      ) : (
                        topSchools.map((row) => (
                          <TableRow key={row.school_code}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={650} noWrap>
                                {row.school_name || row.school_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.school_code}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.total_messages}</TableCell>
                            <TableCell>{row.failed_count}</TableCell>
                            <TableCell>₹{Number(row.total_cost || 0).toFixed(4)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {dailyTrend.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${crmColors.border.default}` }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.25 }}>
                Daily message trend
              </Typography>
              <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 72, overflowX: "auto" }}>
                {dailyTrend.map((row) => {
                  const h = Math.max(8, (Number(row.total_messages) / maxTrend) * 64);
                  return (
                    <Tooltip key={row.day} title={`${row.day}: ${row.total_messages} messages`}>
                      <Box
                        sx={{
                          width: 14,
                          minWidth: 14,
                          height: h,
                          borderRadius: "4px 4px 0 0",
                          bgcolor: crmColors.primary.main,
                          opacity: 0.85,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
});

export default WhatsAppReports;
